import FormSubmitStatus from 'components/FormSubmitStatus';
import { Button } from 'components/Input/Button';
import { InputField } from 'components/Input/InputField';
import PhoneNumberField from 'components/Input/PhoneNumberField';
import { VENDOR_COMMANDS } from 'constants/vendor-commands';
import { FormikHelpers } from 'formik';
import { useConversation } from 'hooks/conversation';
import { useVendorCommand } from 'hooks/useVendorCommand';
import { PlainTextMessage } from 'routes/Chat/Messages/PlainTextMessage';
import { CONTACT_METHOD_TYPE } from '../constants';
import { CONTACT_PROMPT, QUESTION_PROMPT, SUBMITTED, validationSchema } from './constants';
import { ButtonGroup, Fields, StyledForm } from './styles';
import {
  QuestionFormTextOnlyFormType,
  QuestionFormTextOnlyInfoType,
  QuestionFormTextOnlyProps,
} from './types';
import WithDelayedAnimation from 'routes/Chat/Messages/WithDelayedAnimation/WithDelayedAnimation';
import { isLastMessage, isMessageRead } from 'utils/twilio';
import { htmlId } from 'utils/html';
import FormWrapper from 'components/FormWrapper';
import { FORM_IDS } from 'components/FormWrapper/constants';

/**
 * To force trigger in conversation (see Message Templates section in README for more info):
 * message_template_rating_contact_followup_text_only_question_form *
 */
const QuestionFormTextOnly = (props: QuestionFormTextOnlyProps) => {
  const { twilioMessageIndex, form } = props;
  const { isCompleted: isFormPreviouslyCompleted } = form;

  const info = props.form.info as QuestionFormTextOnlyInfoType;

  const initalValues: QuestionFormTextOnlyFormType = {
    name: info?.name ?? '',
    phoneNumber: info?.phone_number ?? '',
    question: info?.question ?? '',
  };

  const { conversationState } = useConversation();
  const { runVendorCommandAsync } = useVendorCommand();

  const isLatestMessage = isLastMessage(twilioMessageIndex, conversationState.messages.length);
  const read = isMessageRead(twilioMessageIndex, conversationState);
  const animate = isLatestMessage && !read;

  const twilioMessageSid = conversationState.messages[twilioMessageIndex].sid;
  const commonAnimationProps = { animate, showIndicator: true };

  const onSubmit = async (
    values: QuestionFormTextOnlyFormType,
    helpers: FormikHelpers<QuestionFormTextOnlyFormType>,
  ) => {
    helpers.setSubmitting(true);
    const result = await runVendorCommandAsync(VENDOR_COMMANDS.CARVANA_CONTACT_FOLLOW_UP_QUESTION, {
      form_id: props.form.id,
      twilio_message_id: twilioMessageSid,
      contact_type: info?.followupType ?? CONTACT_METHOD_TYPE.TEXT,
      name: values.name,
      phone_number: values.phoneNumber,
      question: values.question,
    });
    helpers.setStatus(SUBMITTED);
    if (result?.status !== 200) helpers.setStatus('');
    helpers.setSubmitting(false);
  };

  const onSubmitError = (error: Error, helpers: FormikHelpers<QuestionFormTextOnlyFormType>) => {
    helpers.setStatus(error.message);
  };

  return (
    <>
      <FormWrapper
        id={FORM_IDS.RATING_CONTACT_FOLLOW_UP_QUESTION_ONLY}
        initialValues={initalValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        onError={onSubmitError}
        initialStatus=''
      >
        {({ isValid, dirty, status }) => {
          const inputsDisabled = isFormPreviouslyCompleted || status === SUBMITTED;
          const submitDisabled = inputsDisabled || !dirty || !isValid;
          return (
            <StyledForm noValidate>
              <Fields>
                <WithDelayedAnimation relativeIndex={0} {...commonAnimationProps}>
                  <PlainTextMessage text={QUESTION_PROMPT} />
                </WithDelayedAnimation>
                <WithDelayedAnimation relativeIndex={1} {...commonAnimationProps}>
                  <InputField
                    multiline
                    name='question'
                    id='question'
                    placeholder='Write your question here...'
                    disabled={inputsDisabled}
                  />
                </WithDelayedAnimation>
                <WithDelayedAnimation relativeIndex={2} {...commonAnimationProps}>
                  <PlainTextMessage text={CONTACT_PROMPT} />
                </WithDelayedAnimation>
                <WithDelayedAnimation relativeIndex={3} {...commonAnimationProps}>
                  <>
                    <InputField
                      name='name'
                      id='name'
                      placeholder='Name'
                      label='Name'
                      disabled={inputsDisabled}
                    />
                  </>
                </WithDelayedAnimation>
                <WithDelayedAnimation relativeIndex={4} {...commonAnimationProps}>
                  <PhoneNumberField
                    name='phoneNumber'
                    id='phoneNumber'
                    label='Mobile Phone Number'
                    placeholder='Mobile Phone Number'
                    disabled={inputsDisabled}
                  />
                </WithDelayedAnimation>
              </Fields>

              <WithDelayedAnimation relativeIndex={5} {...commonAnimationProps}>
                <ButtonGroup>
                  {isFormPreviouslyCompleted || status ? (
                    <FormSubmitStatus
                      isSuccess={status === SUBMITTED || isFormPreviouslyCompleted}
                      errorMessage={status}
                    />
                  ) : (
                    <div />
                  )}
                  {!isFormPreviouslyCompleted && status !== SUBMITTED && (
                    <Button
                      id={htmlId('btn-question-form-text-only-submit')}
                      type='submit'
                      size='medium'
                      disabled={submitDisabled}
                    >
                      Submit
                    </Button>
                  )}
                </ButtonGroup>
              </WithDelayedAnimation>
            </StyledForm>
          );
        }}
      </FormWrapper>
    </>
  );
};

export default QuestionFormTextOnly;
